"use client";

import { useEffect, useRef, type ReactNode } from "react";

import { ProductCardWithCart } from "@/app/components/shared/product-card/ProductCardWithCart";
import { useMediaQuery } from "@/app/hooks/useMediaQuery";
import {
	ListingMessage,
	LoadMore,
	LoadPrevious,
	type ListingMessageProps
} from "@/composites/product-listing";
import { ProductGrid } from "@/composites/product";
import { LISTING_PAGE_SIZE } from "@/domain/product-listing/listing-depth";

import {
	LG_UP,
	useListingState,
	type ListingStateValue
} from "../../listing-state";

export interface ListingResultsProps {
	className?: string;
	children: ReactNode;
}

export function ListingResults({
	className,
	children
}: ListingResultsProps): JSX.Element {
	const { isPending } = useListingState();

	return (
		<div
			className={className}
			aria-busy={isPending}
			data-pending={isPending || undefined}
		>
			{children}
		</div>
	);
}

/** The widest grid's first row; later cards load as the shopper scrolls. */
const EAGER_CARDS = 3;

function listingMessage({
	status,
	clearAll,
	retry,
	onward
}: ListingStateValue): ListingMessageProps {
	switch (status) {
		case "noResults":
			return {
				heading: "No products match those filters",
				body: "Try removing a filter.",
				actions: [{ label: "Clear filters", onPress: clearAll }]
			};
		case "error":
			return {
				heading: "We couldn't load products right now",
				body: "Please try again in a moment.",
				actions: [{ label: "Try again", onPress: retry }, onward]
			};
		default:
			return {
				heading: "Nothing to shop here yet",
				body: "Check back soon, or keep browsing.",
				actions: [onward]
			};
	}
}

export function ListingGrid(): JSX.Element {
	const state = useListingState();
	const { cards, firstNewCard, newCardFocused, status } = state;
	const gridRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (firstNewCard === undefined) return;
		gridRef.current
			?.querySelectorAll<HTMLElement>('[data-component="ProductCard"]')
			[firstNewCard]?.querySelector<HTMLElement>("a[href]")
			?.focus();
		newCardFocused();
	}, [firstNewCard, newCardFocused]);

	if (status !== "ready") {
		return <ListingMessage {...listingMessage(state)} />;
	}

	return (
		<div ref={gridRef}>
			<ProductGrid shownBelowLg={state.shownBelowLg}>
				{cards.map(({ product, statusLabel }, index) => (
					<ProductCardWithCart
						key={product.sku}
						product={
							index < EAGER_CARDS
								? product
								: {
										...product,
										image: {
											...product.image,
											loading: "lazy"
										}
									}
						}
						source="plp"
						statusLabel={statusLabel}
						zoomOnHover
					/>
				))}
			</ProductGrid>
		</div>
	);
}

export function ListingLoadMore(): JSX.Element | null {
	const {
		cards,
		firstPosition,
		total,
		loadMore,
		isLoadingMore,
		status,
		pageHref,
		shownBelowLg
	} = useListingState();
	const isLgUp = useMediaQuery(LG_UP, true);

	if (status !== "ready") return null;
	const lastPosition = firstPosition + cards.length - 1;
	const showing = isLgUp
		? cards.length
		: Math.min(shownBelowLg, cards.length);

	return (
		<LoadMore
			first={firstPosition}
			loaded={firstPosition + showing - 1}
			total={total}
			href={pageHref(Math.ceil(lastPosition / LISTING_PAGE_SIZE) + 1)}
			isLoading={isLoadingMore}
			onLoadMore={loadMore}
		/>
	);
}

export function ListingLoadPrevious(): JSX.Element | null {
	const {
		firstPosition,
		hasPrevious,
		loadPrevious,
		isLoadingPrevious,
		status,
		pageHref
	} = useListingState();

	if (status !== "ready" || !hasPrevious) return null;

	return (
		<LoadPrevious
			href={pageHref(Math.ceil(firstPosition / LISTING_PAGE_SIZE) - 1)}
			isLoading={isLoadingPrevious}
			onLoadPrevious={loadPrevious}
		/>
	);
}
